import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface SpaceDetail {
  id: number
  name: string
  description: string | null
  image_url: string | null
  location: string | null
  min_capacity: number
  max_capacity: number
  reservation_notice: string | null
  consent_policy_id: number | null
  is_reservable: number
  is_visible: number
}

interface TimeSlot {
  id: number
  start_time: string
  end_time: string
  label: string | null
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']

function formatTime(t: string) {
  return t.slice(0, 5)
}

function calcDuration(start: string, end: string) {
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  const mins = (eh * 60 + em) - (sh * 60 + sm)
  if (mins <= 0) return ''
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h > 0 && m > 0) return `${h}시간 ${m}분`
  if (h > 0) return `${h}시간`
  return `${m}분`
}

function toDateStr(y: number, m: number, d: number) {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
}

export default function SpaceDetail() {
  const { space_id } = useParams<{ space_id: string }>()
  const navigate = useNavigate()

  const [space, setSpace] = useState<SpaceDetail | null>(null)
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([])
  const [closedSlotIds, setClosedSlotIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)

  const today = new Date()
  const [viewYear, setViewYear] = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1)
  const [selectedDate, setSelectedDate] = useState<Date>(today)
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null)
  const [headcount, setHeadcount] = useState(1)

  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => { fetchSpace() }, [space_id])
  useEffect(() => { if (space) fetchClosedSlots() }, [selectedDate, space])

  const fetchSpace = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
      const [spaceRes, slotRes] = await Promise.all([
        fetch(getApiUrl(`/marubit/api/space/${space_id}`), { headers }),
        fetch(getApiUrl(`/marubit/api/space/admin/${space_id}/time-slots`), { headers }),
      ])
      if (!spaceRes.ok) throw new Error()
      const spaceData = await spaceRes.json()
      setSpace(spaceData)
      setHeadcount(spaceData.min_capacity ?? 1)
      if (slotRes.ok) {
        const slotData = await slotRes.json()
        setTimeSlots(slotData.time_slots ?? [])
      }
    } catch {
      alert('공간 정보를 불러오는 데 실패했습니다.')
      navigate('/space')
    } finally {
      setLoading(false)
    }
  }

  const fetchClosedSlots = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
      const dateStr = toDateStr(selectedDate.getFullYear(), selectedDate.getMonth() + 1, selectedDate.getDate())
      const res = await fetch(
        getApiUrl(`/marubit/api/space/${space_id}/closed-time-slots?reserve_date=${dateStr}`),
        { headers }
      )
      if (res.ok) {
        const data = await res.json()
        setClosedSlotIds((data.closed_time_slots ?? []).map((s: TimeSlot) => s.id))
      }
    } catch { /* ignore */ }
    setSelectedSlotId(null)
  }

  const firstDay = new Date(viewYear, viewMonth - 1, 1).getDay()
  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate()
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  const prevMonth = () => {
    if (viewMonth === 1) { setViewYear(y => y - 1); setViewMonth(12) }
    else setViewMonth(m => m - 1)
  }
  const nextMonth = () => {
    if (viewMonth === 12) { setViewYear(y => y + 1); setViewMonth(1) }
    else setViewMonth(m => m + 1)
  }

  const handleDateClick = (d: number) => {
    const clicked = new Date(viewYear, viewMonth - 1, d)
    if (clicked < todayStart) return
    setSelectedDate(clicked)
  }

  const handleSubmit = () => {
    if (!selectedSlotId) { setErrorMsg('시간을 선택해주세요.'); return }
    setErrorMsg('')
    const dateStr = toDateStr(selectedDate.getFullYear(), selectedDate.getMonth() + 1, selectedDate.getDate())
    const selectedSlot = timeSlots.find(t => t.id === selectedSlotId)
    const timeLabel = selectedSlot ? `${formatTime(selectedSlot.start_time)}~${formatTime(selectedSlot.end_time)}` : ''
    navigate(`/space/${space_id}/apply`, {
      state: {
        reserve_date: dateStr,
        time_slot_id: selectedSlotId,
        time_label: timeLabel,
        headcount,
        space_name: space!.name,
      }
    })
  }

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">불러오는 중...</div>
  )
  if (!space) return null

  const selectedDateStr = toDateStr(selectedDate.getFullYear(), selectedDate.getMonth() + 1, selectedDate.getDate())

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        <button onClick={() => navigate('/space')} className="mb-6 text-[14px] text-gray-400 hover:text-[#887267] transition-colors flex items-center gap-1">
          ← 목록으로
        </button>

        {/* 상단: 이미지 + 기본정보 */}
        <div className="flex flex-col md:flex-row gap-6 mb-6">
          <div className="md:w-[340px] shrink-0">
            <div className="bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.08)]">
              {space.image_url ? (
                <img src={space.image_url} alt={space.name} className="w-full aspect-[4/3] object-cover" />
              ) : (
                <div className="w-full aspect-[4/3] bg-[#ede8e4] flex flex-col items-center justify-center gap-2 text-[#c4b5ad]">
                  <svg viewBox="0 0 24 24" width={36} height={36} fill="none" stroke="currentColor" strokeWidth={1.2}>
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span className="text-[13px]">이미지 없음</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 bg-white rounded-2xl p-7 shadow-[0_2px_12px_rgba(136,114,103,0.08)]">
            <div className="flex items-start justify-between gap-3 mb-4">
              <h1 className="text-[22px] font-extrabold text-[#3d2f2a]">{space.name}</h1>
              {space.is_reservable === 1
                ? <span className="shrink-0 px-3 py-1 text-[12px] font-bold bg-[#887267] text-white rounded-full">예약가능</span>
                : <span className="shrink-0 px-3 py-1 text-[12px] font-bold bg-gray-100 text-gray-400 rounded-full">예약불가</span>
              }
            </div>
            <div className="space-y-3 text-[14px] mb-4">
              {space.location && (
                <div className="flex gap-3"><span className="text-gray-400 w-[80px] shrink-0">위치</span><span className="text-[#3d2f2a]">{space.location}</span></div>
              )}
              <div className="flex gap-3">
                <span className="text-gray-400 w-[80px] shrink-0">이용인원</span>
                <span className="text-[#3d2f2a]">
                  {space.min_capacity === space.max_capacity ? `${space.max_capacity}명` : `${space.min_capacity}~${space.max_capacity}명`}
                </span>
              </div>
            </div>
            {space.description && (
              <>
                <div className="border-t border-[#f0ebe8] mb-3" />
                <h2 className="text-[13px] font-bold text-[#887267] mb-2">공간 소개</h2>
                <p className="text-[13px] text-[#555] leading-relaxed whitespace-pre-line">{space.description}</p>
              </>
            )}
          </div>
        </div>

        {/* 예약 안내 */}
        {space.reservation_notice && (
          <div className="bg-white rounded-2xl p-7 shadow-[0_2px_12px_rgba(136,114,103,0.08)] mb-6">
            <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">예약 안내</h2>
            <p className="text-[14px] text-[#444] leading-relaxed whitespace-pre-line">
              {space.reservation_notice?.split('\n').map((line, i) => {
                const isBold = line.startsWith('**') && line.endsWith('**')
                const text = isBold ? line.slice(2, -2) : line
                return (
                  <span key={i} className={isBold ? 'font-bold text-[#3d2f2a]' : ''}>
                    {text}{'\n'}
                  </span>
                )
              })}
            </p>
          </div>
        )}

        {/* 예약 섹션 */}
        {space.is_reservable === 1 && (
          <div className="bg-white rounded-2xl p-7 shadow-[0_2px_12px_rgba(136,114,103,0.08)] mb-6">
            <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-6">날짜 및 시간 선택</h2>

            <div className="flex flex-col lg:flex-row gap-8">

              {/* 달력 */}
              <div className="lg:w-[340px] shrink-0">
                <div className="flex items-center justify-between mb-4">
                  <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f0ebe8] text-[#887267] transition-colors text-xl font-bold">‹</button>
                  <span className="text-[16px] font-bold text-[#3d2f2a]">{viewYear}년 {viewMonth}월</span>
                  <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f0ebe8] text-[#887267] transition-colors text-xl font-bold">›</button>
                </div>
                <div className="grid grid-cols-7 mb-1">
                  {WEEKDAYS.map((d, i) => (
                    <div key={d} className={`text-center text-[12px] font-semibold py-1 ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400'}`}>{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => {
                    const thisDate = new Date(viewYear, viewMonth - 1, d)
                    const isPast = thisDate < todayStart
                    const isToday = isSameDay(thisDate, today)
                    const isSelected = isSameDay(thisDate, selectedDate)
                    const dow = thisDate.getDay()
                    return (
                      <button
                        key={d}
                        onClick={() => handleDateClick(d)}
                        disabled={isPast}
                        className={[
                          'aspect-square flex items-center justify-center text-[13px] rounded-full m-[2px] transition-colors',
                          isPast ? 'text-gray-200 cursor-not-allowed' :
                          isSelected ? 'bg-[#887267] text-white font-bold' :
                          isToday ? 'border-[1.5px] border-[#887267] text-[#887267] font-bold hover:bg-[#f0ebe8]' :
                          dow === 0 ? 'text-red-400 hover:bg-red-50' :
                          dow === 6 ? 'text-blue-400 hover:bg-blue-50' :
                          'text-[#3d2f2a] hover:bg-[#f0ebe8]',
                        ].join(' ')}
                      >{d}</button>
                    )
                  })}
                </div>
                <div className="flex items-center gap-4 mt-3 text-[12px] text-gray-400">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#887267] inline-block" /> 선택</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full border border-[#887267] inline-block" /> 오늘</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-gray-200 inline-block" /> 예약불가</span>
                </div>
              </div>

              {/* 시간 슬롯 + 인원 + 요약 */}
              <div className="flex-1">
                <p className="text-[15px] font-bold text-[#3d2f2a] mb-4">
                  {selectedDate.getFullYear()}년 {selectedDate.getMonth() + 1}월 {selectedDate.getDate()}일 ({WEEKDAYS[selectedDate.getDay()]}) 시간선택
                </p>

                {timeSlots.length === 0 ? (
                  <p className="text-[14px] text-gray-300 mb-6">등록된 시간 슬롯이 없습니다.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                    {timeSlots.map(slot => {
                      const isSlotPast = (() => {
                        const today = new Date()
                        const slotDate = new Date(selectedDateStr)
                        const [h, m] = slot.start_time.split(':').map(Number)
                        slotDate.setHours(h, m, 0, 0)
                        return today >= slotDate
                      })()
                      const isClosed = closedSlotIds.includes(slot.id) || isSlotPast
                      const isSelected = selectedSlotId === slot.id
                      return (
                        <button
                          key={slot.id}
                          onClick={() => !isClosed && setSelectedSlotId(slot.id)}
                          disabled={isClosed}
                          className={[
                            'rounded-xl px-3 py-3 text-center transition-all border-[1.5px]',
                            isClosed ? 'bg-gray-100 border-gray-100 text-gray-300 cursor-not-allowed' :
                            isSelected ? 'bg-[#887267] border-[#887267] text-white shadow-md' :
                            'bg-white border-[#e5e7eb] text-[#3d2f2a] hover:border-[#887267] hover:bg-[#f9f6f4]',
                          ].join(' ')}
                        >
                          <p className="text-[13px] font-semibold">{formatTime(slot.start_time)}~{formatTime(slot.end_time)}</p>
                            <p className="text-[12px] mt-[2px] opacity-70">
                              {calcDuration(slot.start_time, slot.end_time)}
                            </p>
                          {isClosed && <p className="text-[11px] mt-[2px]">예약불가</p>}
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* 인원 */}
                <div className="mb-5">
                  <p className="text-[14px] font-semibold text-[#887267] mb-2">예약 인원</p>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setHeadcount(h => Math.max(space.min_capacity, h - 1))} className="w-9 h-9 rounded-lg border-[1.5px] border-[#e5e7eb] text-[#887267] font-bold text-lg hover:border-[#887267] transition-colors">-</button>
                    <span className="text-[16px] font-bold text-[#3d2f2a] w-8 text-center">{headcount}</span>
                    <button onClick={() => setHeadcount(h => Math.min(space.max_capacity, h + 1))} className="w-9 h-9 rounded-lg border-[1.5px] border-[#e5e7eb] text-[#887267] font-bold text-lg hover:border-[#887267] transition-colors">+</button>
                    <span className="text-[13px] text-gray-400">({space.min_capacity}~{space.max_capacity}명)</span>
                  </div>
                </div>

                {/* 선택 요약 */}
                <div className="bg-[#f9f6f4] rounded-xl px-5 py-4 text-[13px] space-y-2">
                  <div className="flex gap-3">
                    <span className="text-gray-400 w-[80px] shrink-0">예약일</span>
                    <span className="text-[#3d2f2a] font-semibold">{selectedDateStr} ({WEEKDAYS[selectedDate.getDay()]})</span>
                  </div>
                  <div className="flex gap-3">
                    <span className="text-gray-400 w-[80px] shrink-0">예약 시간</span>
                    <span className="text-[#3d2f2a] font-semibold">
                      {selectedSlotId
                        ? (() => { const s = timeSlots.find(t => t.id === selectedSlotId); return s ? `${formatTime(s.start_time)}~${formatTime(s.end_time)}` : '-' })()
                        : '시간을 선택해주세요.'}
                    </span>
                  </div>
                  <div className="flex gap-3">
                    <span className="text-gray-400 w-[80px] shrink-0">예약 인원</span>
                    <span className="text-[#3d2f2a] font-semibold">{headcount}명</span>
                  </div>
                </div>

                {errorMsg && <p className="text-[13px] text-red-500 mt-3">{errorMsg}</p>}
              </div>
            </div>
          </div>
        )}

        {/* 하단 버튼 */}
        <div className="flex justify-end gap-3">
          <button onClick={() => navigate('/space')} className="h-[48px] px-8 border-[1.5px] border-[#887267] text-[#887267] rounded-xl text-[15px] font-bold hover:bg-[#f0ebe8] transition-colors">
            목록
          </button>
          {space.is_reservable === 1 && (
            <button
              onClick={handleSubmit}

              className="h-[48px] px-10 rounded-xl text-[15px] font-bold transition-colors shadow-[0_4px_16px_rgba(136,114,103,0.2)] bg-[#887267] text-white hover:bg-[#6b5248] cursor-pointer"
            >
              신청하기
            </button>
          )}
        </div>
      </div>

    </div>
  )
}